// A part's bill of materials and routing as a Form (#565): each line names an Item on record, chosen from a list, with a
// quantity for one of the part in a unit that Item is kept in or converts from; each operation is named, in order, and
// done in-house or at a job worker. The Item's page, a material check waiting for the BOM ("Give the BOM") and a BOM
// LAMP drafted in chat all use it. LAMP checks it again before anything is written. Every control is a labelled native
// one, so the Form is filled in from the keyboard.
import { useState, type FormEvent, type ReactNode } from "react";
import type { PartBom, PartBomGiven } from "../api.js";
import { useWords } from "../i18n/words.js";

interface Line {
  readonly component: string;
  readonly quantity: string;
  readonly unit: string;
  readonly scrap_pct: string;
}

interface Operation {
  readonly name: string;
  readonly outsourced: boolean;
  readonly job_worker: string;
}

/** A number typed in a field, or undefined when it is not one. */
const numberIn = (value: string): number | undefined => {
  const n = Number(value.trim());
  return value.trim() === "" || !Number.isFinite(n) ? undefined : n;
};

export interface PartBomFormProps {
  readonly form: PartBom;
  readonly submitLabel: string;
  readonly busy: boolean;
  readonly onSubmit: (given: PartBomGiven) => void;
  /** What else is offered beside the submit button. */
  readonly children?: ReactNode;
}

export function PartBomForm({ form, submitLabel, busy, onSubmit, children }: PartBomFormProps) {
  const w = useWords();
  const [lines, setLines] = useState<readonly Line[]>(
    form.lines.length > 0
      ? form.lines.map((l) => ({
          component: l.component,
          quantity: String(l.quantity),
          unit: l.unit,
          scrap_pct: l.scrap_pct ? String(l.scrap_pct) : "",
        }))
      : [{ component: "", quantity: "", unit: "", scrap_pct: "" }],
  );
  const [operations, setOperations] = useState<readonly Operation[]>(
    form.operations.map((o) => ({ name: o.name, outsourced: o.outsourced, job_worker: o.job_worker ?? "" })),
  );
  const unitsOf = (component: string) => form.items.find((i) => i.id === component)?.units ?? [];
  const change = (at: number, changes: Partial<Line>) =>
    setLines(lines.map((line, i) => (i === at ? { ...line, ...changes } : line)));
  const changeOperation = (at: number, changes: Partial<Operation>) =>
    setOperations(operations.map((op, i) => (i === at ? { ...op, ...changes } : op)));
  const ready =
    lines.length > 0 &&
    lines.every((l) => l.component !== "" && l.unit !== "" && (numberIn(l.quantity) ?? 0) > 0) &&
    operations.every((o) => o.name.trim() !== "");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    onSubmit({
      lines: lines.map((l) => {
        const scrap = numberIn(l.scrap_pct);
        return {
          component: l.component,
          quantity: numberIn(l.quantity) ?? 0,
          unit: l.unit,
          ...(scrap ? { scrap_pct: Math.round(scrap) } : {}),
        };
      }),
      operations: operations.map((o) => ({
        name: o.name.trim(),
        ...(o.job_worker !== "" ? { job_worker: o.job_worker } : o.outsourced ? { outsourced: true } : {}),
      })),
    });
  };

  return (
    <form className="set-up" onSubmit={submit}>
      <table className="lines">
        <caption>{w.bomForOne(form.part.name, form.part.unit)}</caption>
        <thead>
          <tr>
            <th scope="col">{w.setUpItem}</th>
            <th scope="col">{w.setUpQuantity}</th>
            <th scope="col">{w.setUpUnit}</th>
            <th scope="col">{w.setUpScrap}</th>
            <td />
          </tr>
        </thead>
        <tbody>
          {lines.map((line, i) => {
            const n = i + 1;
            const units = unitsOf(line.component);
            return (
              <tr key={i}>
                <td>
                  <select
                    aria-label={`${w.setUpLine(n)}: ${w.setUpItem}`}
                    value={line.component}
                    onChange={(e) => {
                      const next = unitsOf(e.target.value);
                      change(i, {
                        component: e.target.value,
                        unit: next.includes(line.unit) ? line.unit : (next[0] ?? ""),
                      });
                    }}
                  >
                    <option value="">{w.setUpChoose}</option>
                    {form.items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    aria-label={`${w.setUpLine(n)}: ${w.setUpQuantity}`}
                    inputMode="decimal"
                    value={line.quantity}
                    onChange={(e) => change(i, { quantity: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    aria-label={`${w.setUpLine(n)}: ${w.setUpUnit}`}
                    value={line.unit}
                    onChange={(e) => change(i, { unit: e.target.value })}
                  >
                    {(units.includes(line.unit) || line.unit === "" ? units : [line.unit, ...units]).map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    aria-label={`${w.setUpLine(n)}: ${w.setUpScrap}`}
                    inputMode="numeric"
                    value={line.scrap_pct}
                    onChange={(e) => change(i, { scrap_pct: e.target.value })}
                  />
                </td>
                <td>
                  <button type="button" className="linkish" onClick={() => setLines(lines.filter((_, at) => at !== i))}>
                    {w.setUpRemoveLine(n)}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        type="button"
        className="btn"
        onClick={() => setLines([...lines, { component: "", quantity: "", unit: "", scrap_pct: "" }])}
      >
        {w.setUpAddLine}
      </button>
      <table className="lines">
        <caption>{w.bomRouting}</caption>
        <thead>
          <tr>
            <th scope="col">{w.setUpName}</th>
            <th scope="col">{w.bomOutsourced}</th>
            <th scope="col">{w.bomJobWorker}</th>
            <td />
          </tr>
        </thead>
        <tbody>
          {operations.map((op, i) => {
            const n = i + 1;
            return (
              <tr key={i}>
                <td>
                  <input
                    aria-label={`${w.setUpOperation(n)}: ${w.setUpName}`}
                    value={op.name}
                    maxLength={200}
                    onChange={(e) => changeOperation(i, { name: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`${w.setUpOperation(n)}: ${w.bomOutsourced}`}
                    checked={op.outsourced || op.job_worker !== ""}
                    onChange={(e) =>
                      changeOperation(i, {
                        outsourced: e.target.checked,
                        ...(e.target.checked ? {} : { job_worker: "" }),
                      })
                    }
                  />
                </td>
                <td>
                  <select
                    aria-label={`${w.setUpOperation(n)}: ${w.bomJobWorker}`}
                    value={op.job_worker}
                    disabled={!op.outsourced && op.job_worker === ""}
                    onChange={(e) => changeOperation(i, { job_worker: e.target.value })}
                  >
                    <option value="">{w.setUpChoose}</option>
                    {form.jobWorkers.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => setOperations(operations.filter((_, at) => at !== i))}
                  >
                    {w.setUpRemoveOperation(n)}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        type="button"
        className="btn"
        onClick={() => setOperations([...operations, { name: "", outsourced: false, job_worker: "" }])}
      >
        {w.setUpAddOperation}
      </button>
      <div className="acts">
        <button type="submit" className="btn btn-go" disabled={busy || !ready}>
          {submitLabel}
        </button>
        {children}
      </div>
    </form>
  );
}

/** A part's BOM and routing to read, for a person who may not change them. */
export function PartBomView({ form }: { readonly form: PartBom }) {
  const w = useWords();
  const named = new Map(form.jobWorkers.map((j) => [j.id, j.name]));
  if (form.lines.length === 0) return <p className="muted">{w.bomNone}</p>;
  return (
    <>
      <table className="lines">
        <caption>{w.bomForOne(form.part.name, form.part.unit)}</caption>
        <thead>
          <tr>
            <th scope="col">{w.setUpItem}</th>
            <th scope="col">{w.setUpQuantity}</th>
            <th scope="col">{w.setUpUnit}</th>
            <th scope="col">{w.setUpScrap}</th>
          </tr>
        </thead>
        <tbody>
          {/* By position: one Item may be on two lines (#668). */}
          {form.lines.map((l, i) => (
            <tr key={i}>
              <th scope="row">{l.name}</th>
              <td>{l.quantity}</td>
              <td>{l.unit}</td>
              <td>{l.scrap_pct ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {form.operations.length > 0 ? (
        <ol aria-label={w.bomRouting}>
          {form.operations.map((o, i) => (
            <li key={i}>
              {o.name}
              {o.job_worker
                ? ` — ${named.get(o.job_worker) ?? w.bomOutsourced}`
                : o.outsourced
                  ? ` — ${w.bomOutsourced}`
                  : ""}
            </li>
          ))}
        </ol>
      ) : null}
    </>
  );
}
